import { motion, useInView } from 'framer-motion';
import { useEffect, useRef, useState, lazy, Suspense } from 'react';
import { Star, PlayCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent } from './ui/dialog';
import { AspectRatio } from './ui/aspect-ratio';

// react-pdf pesa bastante (~100KB) - solo se descarga cuando el cliente
// realmente abre la carta completa, nunca en la carga inicial de la web.
const PdfViewerModal = lazy(() => import('./PdfViewerModal'));

const Menu = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [activeCategory, setActiveCategory] = useState('todos');
  const [categories, setCategories] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [activeVideo, setActiveVideo] = useState(null);
  const [menuPdf, setMenuPdf] = useState(null);
  const [showPdf, setShowPdf] = useState(false);

  useEffect(() => {
    fetch('/api/categories.php')
      .then((res) => res.json())
      .then(setCategories)
      .catch(() => setCategories([]));

    fetch('/api/menu_items.php?visible=1')
      .then((res) => res.json())
      .then(setMenuItems)
      .catch(() => setMenuItems([]));

    fetch('/api/menu_pdf.php')
      .then((res) => res.json())
      .then((data) => setMenuPdf(data.exists ? data : null))
      .catch(() => setMenuPdf(null));
  }, []);

  const menuCategories = [{ id: 'todos', name: 'Todos' }, ...categories.map((c) => ({ id: c.slug, name: c.name }))];

  const filteredItems = activeCategory === 'todos'
    ? menuItems
    : menuItems.filter(item => item.category_slug === activeCategory);

  return (
    <section
      id="carta"
      data-testid="menu-section"
      ref={ref}
      className="py-20 md:py-32 bg-cream-alt relative"
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-30" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23C04000' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
      }} />

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <span className="text-terracotta font-medium uppercase tracking-widest text-sm">
            Nuestra Carta
          </span>
          <h2
            className="font-heading text-4xl md:text-5xl font-bold text-coffee mt-4 mb-6"
            data-testid="menu-title"
          >
            Sabores del Perú
          </h2>
          <p className="text-coffee-light text-lg max-w-2xl mx-auto">
            Descubre nuestra selección de platos tradicionales y fusión,
            preparados con ingredientes frescos y la pasión de nuestra cocina.
          </p>
        </motion.div>

        {/* Category Filter */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="flex flex-wrap justify-center gap-3 mb-12"
          data-testid="menu-categories"
        >
          {menuCategories.map((category) => (
            <button
              key={category.id}
              onClick={() => setActiveCategory(category.id)}
              className={`px-6 py-2 rounded-full font-medium transition-all duration-300 ${
                activeCategory === category.id
                  ? 'bg-terracotta text-white shadow-lg'
                  : 'bg-white text-coffee-light hover:bg-terracotta/10 border border-border'
              }`}
              data-testid={`menu-category-${category.id}`}
            >
              {category.name}
            </button>
          ))}
        </motion.div>

        {/* Menu Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredItems.map((item, index) => (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.1 * index }}
              className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 group hover:-translate-y-2"
              data-testid={`menu-item-${item.id}`}
            >
              {/* Image */}
              <div className="relative h-52 overflow-hidden">
                <img
                  src={item.image_url}
                  alt={item.name}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-coffee/40 to-transparent" />

                {/* Video play icon + label: bottom-left corner, dark layer only behind the badge itself */}
                {item.video_url && (
                  <button
                    onClick={() => setActiveVideo(item.video_url)}
                    className="absolute bottom-4 left-4 flex flex-col items-center gap-0.5 px-2.5 py-1.5 rounded-lg bg-black/55 hover:bg-black/75 transition-colors"
                    aria-label={`Ver video de ${item.name}`}
                    data-testid={`menu-item-video-${item.id}`}
                  >
                    <PlayCircle className="w-5 h-5 text-white" />
                    <span className="text-[10px] font-semibold text-white leading-none whitespace-nowrap">
                      Ver Video
                    </span>
                  </button>
                )}

                {/* Badges */}
                <div className="absolute top-4 left-4 flex gap-2">
                  {item.is_popular && (
                    <span className="bg-gold text-coffee text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1">
                      <Star className="w-3 h-3" /> Popular
                    </span>
                  )}
                </div>

                {/* Price */}
                <div className="absolute bottom-4 right-4">
                  <span className="bg-white/95 backdrop-blur-sm text-terracotta font-bold text-lg px-4 py-2 rounded-full shadow-lg">
                    {item.price}€
                  </span>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <h3 className="font-heading font-semibold text-xl text-coffee mb-2 group-hover:text-terracotta transition-colors">
                  {item.name}
                </h3>
                <p className="text-coffee-light text-sm leading-relaxed">
                  {item.description}
                </p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="text-center mt-16"
        >
          <p className="text-coffee-light mb-6">
            ¿Quieres ver nuestra carta completa?
          </p>
          {menuPdf ? (
            <Button
              onClick={() => setShowPdf(true)}
              className="bg-terracotta hover:bg-terracotta-dark text-white rounded-full px-8 py-6 text-lg font-medium transition-all duration-300 hover:scale-105 shadow-lg"
              data-testid="menu-pdf-btn"
            >
              Ver carta completa
            </Button>
          ) : (
            <Button
              asChild
              className="bg-terracotta hover:bg-terracotta-dark text-white rounded-full px-8 py-6 text-lg font-medium transition-all duration-300 hover:scale-105 shadow-lg"
              data-testid="menu-whatsapp-btn"
            >
              <a
                href="https://wa.me/34623513773?text=Hola,%20me%20gustaría%20ver%20la%20carta%20completa"
                target="_blank"
                rel="noopener noreferrer"
              >
                Consultar Carta Completa
              </a>
            </Button>
          )}
        </motion.div>
      </div>

      {/* Video modal */}
      <Dialog open={!!activeVideo} onOpenChange={(open) => !open && setActiveVideo(null)}>
        <DialogContent className="max-w-3xl p-0 overflow-hidden bg-black">
          {activeVideo && (
            <AspectRatio ratio={16 / 9}>
              <video
                key={activeVideo}
                src={activeVideo}
                controls
                autoPlay
                className="w-full h-full"
                data-testid="menu-video-player"
              />
            </AspectRatio>
          )}
        </DialogContent>
      </Dialog>

      {/* PDF completo de la carta - se carga solo cuando el cliente lo abre */}
      {menuPdf && showPdf && (
        <Suspense fallback={null}>
          <PdfViewerModal
            open={showPdf}
            onOpenChange={setShowPdf}
            url={menuPdf.url}
            title="Carta completa"
          />
        </Suspense>
      )}
    </section>
  );
};

export default Menu;
